import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { stripeErrorToast } from '../utils/stripeErrors';
import { Download, FileSpreadsheet, Loader2, Crown, ChevronRight, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BalanceCards } from '../components/earnings/BalanceCards';
import { FeeBreakdown } from '../components/earnings/FeeBreakdown';
import { TransactionsTable } from '../components/earnings/TransactionsTable';
import { PayoutHistory } from '../components/earnings/PayoutHistory';
import { TopClientsCard } from '../components/earnings/TopClientsCard';
import { StripeConnectCard } from '../components/provider/StripeConnectCard';
import { trackUpgradeEvent } from '../utils/upgradeTracker';

const PRO_FEATURES = [
  '8% commission on new clients only',
  '0% commission on repeat clients',
  'Lower processing fees (1.55% + 20p)',
  'Prospect messaging, reply to new enquiries',
  'Smart Waitlists with SMS auto-fill',
  'HMRC-ready VAT & sales reports',
  'Advanced analytics dashboard',
  'Priority support',
];

export default function EarningsTab() {
  const { betaConfig } = useAuth();
  const betaMode = !!betaConfig?.beta_mode;
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchEarnings();
  }, []);

  // Log banner impression once tier is known, tracked from a top-level hook
  // (NOT after conditional returns) so React hook order stays consistent.
  useEffect(() => {
    if (earnings && earnings.tier !== 'pro') {
      trackUpgradeEvent('impression', 'earnings_banner');
    }
  }, [earnings]);

  const fetchEarnings = async () => {
    try {
      const [earningsRes, payoutsRes] = await Promise.all([
        api.get('/providers/me/earnings'),
        api.get('/providers/me/payouts')
      ]);
      setEarnings(earningsRes.data);
      setPayouts(payoutsRes.data || []);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your Pro subscription? You\'ll keep Pro features until the end of your current billing period.')) return;
    try {
      await api.post('/subscriptions/cancel');
      toast.success('Subscription will cancel at end of billing period');
      fetchEarnings();
    } catch (error) {
      toast.error(stripeErrorToast(error, 'Failed to cancel subscription'));
    }
  };


  const handleDownloadReport = async (period) => {
    setDownloadingReport(period);
    try {
      const response = await api.get(`/providers/me/report?period=${period}`, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `zuriya_report_${period}.csv`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${period === 'quarterly' ? 'Quarterly' : 'Monthly'} report downloaded`);
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(null);
    }
  };

  const handleDownloadHmrc = async () => {
    setDownloadingReport('hmrc');
    try {
      const response = await api.get('/providers/me/report/hmrc', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'zuriya_hmrc_report_2025-26.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('HMRC report downloaded');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('HMRC reports are available on the Pro plan');
        setUpgradeOpen(true);
      } else {
        toast.error('Failed to download report');
      }
    } finally {
      setDownloadingReport(null);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    // Log the intent, the actual checkout redirect follows. Paired with a
    // post-checkout impression trigger it lets us measure checkout-start vs
    // checkout-complete drop-off separately from click → intent.
    trackUpgradeEvent('upgrade', 'earnings_dialog');
    try {
      const res = await api.post('/subscriptions/checkout');
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start upgrade');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!earnings) {
    return (
      <div className="card-surface p-8 text-center">
        <p className="text-stone-500">Unable to load earnings data.</p>
      </div>
    );
  }

  const isPro = earnings.tier === 'pro';
  const progressPercent = Math.min(
    (earnings.available_for_payout / earnings.payout_threshold) * 100,
    100
  );

  const openUpgradeDialog = (surface) => {
    trackUpgradeEvent('click', surface);
    trackUpgradeEvent('impression', 'earnings_dialog');
    setUpgradeOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="earnings-tab">
      {/* Stripe Connect payout method, top priority so providers see it first */}
      <StripeConnectCard />

      {/* Tier Banner */}
      {!isPro && !betaMode && (
        <div className="relative overflow-hidden rounded-xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#1A1A1A] to-[#2A2520] p-4 sm:p-5" data-testid="pro-upgrade-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="rounded-lg bg-[#D4AF37]/20 p-2 shrink-0">
                <Crown className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Upgrade to Pro, £25/month</p>
                <p className="text-xs text-stone-400 mt-0.5">Unlocks prospect messaging, Smart Waitlists, and 47% lower commission</p>
              </div>
            </div>
            <Button onClick={() => openUpgradeDialog('earnings_banner')} className="bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#c9a030] font-medium gap-1 shrink-0" data-testid="upgrade-pro-btn">
              Upgrade <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {isPro && !betaMode && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-[#F9F6F0] border border-[#D4AF37]/30 rounded-lg" data-testid="pro-badge">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Crown className="h-4 w-4 text-[#D4AF37] shrink-0" />
            <span className="text-sm font-medium text-[#1A1A1A]">Pro Plan</span>
            <span className="text-xs text-stone-400">8% on new clients &middot; 1.55% + 20p processing</span>
          </div>
          <button onClick={handleCancelSubscription} className="text-xs text-stone-400 hover:text-red-500 transition-colors shrink-0" data-testid="cancel-subscription-btn">
            Cancel plan
          </button>
        </div>
      )}

      <BalanceCards earnings={earnings} />

      {/* Payout Progress Bar */}
      <div className="card-surface p-4 sm:p-6" data-testid="payout-progress">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-medium text-[#1A1A1A]">Payout Progress</h3>
          <span className="text-xs text-stone-400">
            Minimum: £{earnings.payout_threshold.toFixed(0)} &middot; {earnings.payout_cycle} cycle
          </span>
        </div>
        <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${progressPercent}%`,
              background: earnings.meets_payout_threshold
                ? 'linear-gradient(90deg, #10b981, #059669)'
                : 'linear-gradient(90deg, #D4AF37, #c9a030)',
            }}
            data-testid="payout-progress-bar"
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-stone-400">
          <span>£0</span>
          <span>£{earnings.payout_threshold.toFixed(0)}</span>
        </div>
        {!earnings.meets_payout_threshold && earnings.available_for_payout > 0 && (
          <p className="text-xs text-stone-500 mt-2">
            Balance below £{earnings.payout_threshold.toFixed(0)} rolls over to the next payout cycle automatically.
          </p>
        )}
      </div>

      <FeeBreakdown earnings={earnings} />

      <TopClientsCard onUpgrade={() => openUpgradeDialog('top_clients_card')} />

      <TransactionsTable transactions={earnings.recent_transactions} />

      <PayoutHistory
        payouts={payouts}
        payoutThreshold={earnings.payout_threshold}
        nextPayoutAt={earnings.next_payout_at}
      />

      {/* Download Reports */}
      <div className="card-surface p-4 sm:p-6" data-testid="download-reports">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-[#D4AF37]" />
          Download Reports
        </h3>
        <p className="text-xs text-stone-400 mb-4">Download a CSV breakdown of your bookings, earnings, and fees.</p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => handleDownloadReport('monthly')}
            disabled={downloadingReport === 'monthly'}
            className="gap-2 border-[#E6E4DD]"
            data-testid="download-monthly-report-btn"
          >
            {downloadingReport === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Last Month
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDownloadReport('quarterly')}
            disabled={downloadingReport === 'quarterly'}
            className="gap-2 border-[#E6E4DD]"
            data-testid="download-quarterly-report-btn"
          >
            {downloadingReport === 'quarterly' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Quarterly Report
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadHmrc}
            disabled={downloadingReport === 'hmrc'}
            className={`gap-2 ${isPro ? 'border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50' : 'border-[#E6E4DD] text-stone-400'}`}
            data-testid="download-hmrc-report-btn"
          >
            {downloadingReport === 'hmrc' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
            HMRC Report {!isPro && <span className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-1.5 py-0.5 rounded-full font-medium">PRO</span>}
          </Button>
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Crown className="h-5 w-5 text-[#D4AF37]" /> Upgrade to Pro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1A1A1A]">£25<span className="text-base font-normal text-stone-400">/month</span></p>
              <p className="text-xs text-stone-500 mt-1.5" data-testid="upgrade-dialog-reinforcement">
                Unlocks prospect messaging, Smart Waitlists, and 47% lower commission
              </p>
            </div>
            <ul className="space-y-2.5">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-stone-600">
                  <div className="w-5 h-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ChevronRight className="h-3 w-3 text-[#D4AF37]" />
                  </div>
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-stone-400 text-center">
              Flat processing rates across all card types: 1.75% + 20p (Free) or 1.55% + 20p (Pro). No surprises at payout.
            </p>
            <Button onClick={handleUpgrade} disabled={upgrading} className="w-full btn-primary gap-2" data-testid="confirm-upgrade-btn">
              {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
              {upgrading ? 'Upgrading...' : 'Upgrade to Pro, £25/month'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
